import { useEffect, useState } from 'react'
import Maps from './Maps';
import { GetCurrent, GetEmail, GetMonitoredObject, GetSetup } from '../../Api/getData';
import TableMonitoring from './TableMonitoring';
import dayjs from 'dayjs';
import { HiChartBar, HiClock, HiOfficeBuilding, HiMap, HiRefresh } from 'react-icons/hi';

const Monitoring = () => {

    useEffect(() => {
        document.title = "NextSense - Monitoring";
    }, []);

    const [dataShip, setDataShip] = useState([]);
    const [dataJoined, setDataJoined] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [loadingRows, setLoadingRows] = useState({});


    const fetchAndJoinData = async () => {
        try {
            setIsLoading(true);
            setDataShip([]);
            setDataJoined([]);
            setLoadingRows({});

            // Fetch ship list
            let shipList = [];
            try {
                const shipRes = await GetMonitoredObject();
                shipList = shipRes?.Ship || [];
            } catch (error) {
                shipList = [];
                setIsLoading(false);
                return;
            }

            const setupList = await GetSetup();
            const filteredSetupList = setupList.filter(item => item.AccountId !== 'SINERGI');

            // Fetch email data
            const emailRes = await Promise.all(
                filteredSetupList.map(async (item) => {
                    try {
                        const emailData = await GetEmail({ ObjectCode: item.ObjectCode });

                        return {
                            AccountId: item.AccountId,
                            ObjectCode: item.ObjectCode,
                            email: emailData
                        };
                    } catch (error) {
                        return {
                            AccountId: item.AccountId,
                            ObjectCode: item.ObjectCode,
                            email: []
                        };
                    }
                })
            );

            // console.log('emailRes:', emailRes);

            // try {
            //     emailRes = await GetAllEmail();
            // } catch (error) {
            //     emailRes = [];
            // }

            // Set initial loading state untuk semua ship
            const initialLoadingState = {};
            shipList.forEach(ship => {
                initialLoadingState[ship.ShipCode] = true;
            });
            setLoadingRows(initialLoadingState);
            setIsLoading(false);

            // Fetch current data for each ship progressively
            const tempDataShip = [];
            const tempDataJoined = [];

            for (let i = 0; i < shipList.length; i++) {
                const ship = shipList[i];

                try {
                    const currentData = await GetCurrent({
                        AccountId: ship.KodeCabang,
                        ObjectCode: ship.ShipCode
                    });

                    // Only add if current data is valid
                    if (currentData && currentData.length > 0) {
                        const matchingEmail = emailRes.find(
                            email => email.ObjectCode === ship.ShipCode
                        );

                        const shipWithCurrent = {
                            ...ship,
                            current: currentData
                        };

                        const joinedItem = {
                            ...ship,
                            email: matchingEmail.email || [],
                            current: currentData
                        };

                        // console.log('matchingEmail:', matchingEmail);

                        tempDataShip.push(shipWithCurrent);
                        tempDataJoined.push(joinedItem);

                        // Update state progressively - tampilkan data yang sudah berhasil
                        setDataShip([...tempDataShip]);
                        setDataJoined([...tempDataJoined]);

                        // Update last update time
                        if (currentData[0]?.RefDate) {
                            setLastUpdate(currentData[0].RefDate);
                        }
                    }

                    // Remove loading state untuk ship ini
                    setLoadingRows(prev => {
                        const newState = { ...prev };
                        delete newState[ship.ShipCode];
                        return newState;
                    });
                } catch (error) {
                    // Remove loading state untuk ship yang error
                    setLoadingRows(prev => {
                        const newState = { ...prev };
                        delete newState[ship.ShipCode];
                        return newState;
                    });
                    continue;
                }
            }

        } catch (error) {
            console.log('Error fetching data:', error)
        } finally {
            setIsLoading(false);
            setLoadingRows({});
        }
    };

    // console.log("Data dataJoined:", dataJoined);


    useEffect(() => {

        let intervalId;

        fetchAndJoinData(); // Call immediately

        intervalId = setInterval(fetchAndJoinData, 1800000); // Every 30 minutes

        return () => clearInterval(intervalId); // Cleanup on unmount
    }, []);


    const handleRefresh = () => {
        fetchAndJoinData();
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
            <div className="space-y-6">
                {/* Header Section */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg">
                                <HiChartBar className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="lg:text-3xl text-2xl font-bold gradient-text">Marine Monitoring</h1>
                                <p className="text-gray-600 mt-1">Real-time vessel tracking and sensor data</p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            {/* Status Indicators */}
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2 px-4 py-2 bg-success-50 rounded-xl border border-success-200">
                                    <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm font-semibold text-success-700">Live Data</span>
                                </div>
                                <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-xl border border-blue-200">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                    <span className="text-sm font-semibold text-blue-700">{dataShip.length} Vessels</span>
                                </div>
                            </div>

                            {/* Last Update */}
                            {lastUpdate && (
                                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-xl border border-gray-200">
                                    <HiClock className="w-4 h-4 text-gray-500" />
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Last Updated</p>
                                        <p className="text-sm font-semibold text-gray-700">
                                            {dayjs(lastUpdate).format("DD MMM YYYY, HH:mm:ss")}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Ship List - Left Column */}
                    <div className="xl:col-span-1">
                        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 h-[750px] flex flex-col">
                            <div className="p-6 border-b border-gray-100/50 flex-shrink-0 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                        <HiOfficeBuilding className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900">Active Vessels</h2>
                                        <p className="text-sm text-gray-600">Currently monitored ships</p>
                                    </div>
                                </div>
                                <button onClick={handleRefresh} className="p-2 rounded-lg border border-blue-200 hover:bg-blue-50 transition-all duration-300 cursor-pointer">
                                    {isLoading ? <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div> : (
                                        <HiRefresh className="w-5 h-5 text-blue-600 hover:text-blue-700 transition-all duration-300" />
                                    )}
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                <TableMonitoring
                                    dataSource={dataShip}
                                    isLoading={isLoading}
                                    loadingRows={loadingRows}
                                    updateData={handleRefresh} />
                            </div>
                        </div>
                    </div>

                    {/* Map - Right Column */}
                    <div className="xl:col-span-2">
                        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 h-[750px] flex flex-col">
                            <div className="p-6 border-b border-gray-100/50 flex-shrink-0">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                                        <HiMap className="w-4 h-4 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900">Vessel Locations</h2>
                                        <p className="text-sm text-gray-600">Interactive map with real-time positions</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-1">
                                <Maps dataSource={dataJoined} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sensor Monitoring */}
                {/* {!isLoading && dataShip.length > 0 && (
                    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20">
                        <div className="p-6 border-b border-gray-100/50">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                    <HiCube className="w-4 h-4 text-purple-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Sensor Data</h2>
                                    <p className="text-sm text-gray-600">Live sensor readings and status</p>
                                </div>
                            </div>
                        </div>
                        <SensorMonitoring dataSource={dataShip} />
                    </div>
                )} */}
            </div>
        </div>
    )
}

export default Monitoring